import {
  buildClinicalTaskEditOps,
  ClinicalTaskEditFields,
  getClinicalTaskAssignee,
} from '@bariendo/lib/clinical-tasks';
import { createReference, getReferenceString } from '@medplum/core';
import { CareTeam, Reference, Task } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback } from 'react';
import { alert } from '@/services/alert';
import { useGuardedTaskPatch } from './useGuardedTaskPatch';

/**
 * Edits a clinical task's user-editable fields in place via the guarded
 * patch protocol (see useGuardedTaskPatch). Returns the updated Task, or
 * undefined when the write was refused or conflicted (the executor has
 * already alerted and refreshed in that case).
 */
export function useTaskEdit(onChange?: () => void | Promise<void>) {
  const medplum = useMedplum();
  const { patchTask, isUpdating } = useGuardedTaskPatch(onChange);

  const editTask = useCallback(
    async (task: Task, fields: ClinicalTaskEditFields): Promise<Task | undefined> => {
      // Switching an individually assigned task back to a role hands
      // ownership back to the patient's CareTeam, or leaves it unowned when
      // the patient has none (mirrors create-clinical-task).
      let careTeam: Reference<CareTeam> | null | undefined;
      if (fields.performer && getClinicalTaskAssignee(task)) {
        const patientRef = task.for ? getReferenceString(task.for) : undefined;
        if (!patientRef) {
          alert.error('Cannot reassign task: it has no patient.');
          return undefined;
        }
        const found = await medplum.searchOne('CareTeam', { subject: patientRef });
        careTeam = found ? createReference(found) : null;
      }

      return patchTask(task, buildClinicalTaskEditOps(task, fields, { careTeam }));
    },
    [medplum, patchTask]
  );

  return { editTask, isUpdating };
}
